import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { useMaintenance } from '../contexts/MaintenanceContext';

const MaintenanceModal = ({ isOpen, onClose }) => {
  const [reason, setReason] = useState('');
  const [estimatedEnd, setEstimatedEnd] = useState('');
  const [loading, setLoading] = useState(false);
  const { activateMaintenance } = useMaintenance();

  const handleActivate = async () => {
    if (!reason.trim()) {
      toast.error('La raison est obligatoire');
      return;
    }

    setLoading(true);
    try {
      await activateMaintenance(reason, estimatedEnd);
      toast.success('Mode maintenance activé');
      setReason('');
      setEstimatedEnd('');
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'activation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <AlertTriangle className="h-6 w-6 text-amber-500" />
            Activer le Mode Maintenance
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Information Block */}
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 space-y-2">
            <p className="text-sm">
              L'application sera <span className="font-semibold">inaccessible</span> pour :
            </p>
            <ul className="text-sm space-y-1 ml-4">
              <li>• Tous les <span className="font-semibold">Utilisateurs</span> non-administrateurs</li>
            </ul>
            <p className="text-sm mt-3">
              <span className="font-semibold">Administrateurs</span> conserveront l'accès.
            </p>
          </div>

          {/* Reason Field */}
          <div className="space-y-2">
            <label className="text-sm font-medium">
              Raison <span className="text-red-500">*</span>
            </label>
            <Textarea
              placeholder="Ex: Inventaire en cours"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="resize-none"
            />
          </div>

          {/* Estimated End Field */}
          <div className="space-y-2">
            <label className="text-sm font-medium">
              Fin estimée <span className="text-muted-foreground">(optionnel)</span>
            </label>
            <Input
              type="datetime-local"
              value={estimatedEnd}
              onChange={(e) => setEstimatedEnd(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter className="flex gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            Annuler
          </Button>
          <Button
            variant="destructive"
            onClick={handleActivate}
            disabled={loading}
          >
            {loading ? 'Activation...' : 'Activer la Maintenance'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MaintenanceModal;
